import { useBoolean } from '@stenajs-webui/core';
import { fetchBooking } from '../../../../common/api';
import type { BasicBooking } from '../../../../common/graphql/fragments/gql/BasicBooking';
import { type TrackerCategory, trackEvent } from '../../../../common/tracking/trackerService';
import { useUpdateFormStateMinimalBooking } from '../../../../common/utils/minimalBooking';
import { ImportExportStatusCode } from '../../../../gql/graphql';
import { initializeUpdateFormWithBooking } from '../../../../services/update-form/updateFormActionsHelpers';
import { useAppDispatch } from '../../../../store/appDispatch';
import { CustomsLinkPopupContent } from '../../../customslink/components/popup/CustomsLinkPopupContent';
import { CUSTOMS_LINK_LABEL_SCAC } from '../../../customslink/utils/constants';
import {
  getShowCustomsLinkForEnvelopeId,
  getShowCustomsLinkForScac,
} from '../../../customslink/utils/showCustomsLinkUtil';
import { ImportExportStatusCellIconButton } from '../../../import-export/components/ImportExportStatusCellIconButton';
import {
  getImportExportSettingsFromMinimalBooking,
  getImportExportSettingsFromRoute,
} from '../../../import-export/util/ImportExportSettingsGetters';

interface ImportExportLabelIconProps {
  booking: BasicBooking;
  name: 'importReference' | 'exportReference';
  trackerCategory: TrackerCategory;
}

export function ImportExportLabelIcon({
  booking,
  name,
  trackerCategory,
}: ImportExportLabelIconProps) {
  const minimalBooking = useUpdateFormStateMinimalBooking();
  const dispatch = useAppDispatch();
  const [visible, openPopover, closePopover] = useBoolean(false);

  const importExportSettings = getImportExportSettingsFromMinimalBooking(
    minimalBooking,
    getImportExportSettingsFromRoute(minimalBooking?.route, name),
  );

  const reference = booking[name];

  const trackOpenPopover = () => {
    trackEvent({
      category: trackerCategory,
      action: 'Brexit icon clicked',
      label: importExportSettings ? importExportSettings.type : '',
    });
    openPopover();
  };

  const onRefreshBooking = async () => {
    trackEvent({
      category: trackerCategory,
      action: 'Refresh import export status',
      label: ImportExportStatusCode.Refresh,
    });
    const bookingNo = booking.bookingNo;
    const fetchBookingResult = await fetchBooking(bookingNo);
    const newBooking = fetchBookingResult.success ? fetchBookingResult.value : null;
    if (newBooking) {
      dispatch(initializeUpdateFormWithBooking(newBooking, 'Details'));
    }
  };

  if (!importExportSettings || importExportSettings.type === 'Empty') {
    return null;
  }

  if (importExportSettings.type === 'EnvelopeId') {
    return (
      <ImportExportStatusCellIconButton
        onRefreshIconClick={onRefreshBooking}
        bookingNo={booking.bookingNo}
        status={reference.status}
        visible={visible}
        onOpen={trackOpenPopover}
        onRequestClose={closePopover}
        hideEmptyIcon
      >
        {getShowCustomsLinkForEnvelopeId(
          booking,
          importExportSettings.name,
          reference.reference,
        ) && (
          <CustomsLinkPopupContent
            onRequestClose={closePopover}
            booking={booking}
            label={importExportSettings.name}
            trackerCategory={trackerCategory}
          />
        )}
      </ImportExportStatusCellIconButton>
    );
  }

  if (importExportSettings.type === 'Ens') {
    return (
      <ImportExportStatusCellIconButton
        status={reference.status}
        bookingNo={booking.bookingNo}
        visible={visible}
        onOpen={openPopover}
        onRequestClose={closePopover}
        hideEmptyIcon
      />
    );
  }

  if (importExportSettings.type === 'Scac') {
    return (
      <ImportExportStatusCellIconButton
        status={reference.status}
        bookingNo={booking.bookingNo}
        showPopupRefreshStatusButton={
          reference.status?.statusCode === ImportExportStatusCode.Refresh
        }
        visible={visible}
        onOpen={trackOpenPopover}
        onRequestClose={closePopover}
        hideEmptyIcon
      >
        {getShowCustomsLinkForScac(booking) && (
          <CustomsLinkPopupContent
            onRequestClose={closePopover}
            booking={booking}
            label={CUSTOMS_LINK_LABEL_SCAC}
            trackerCategory={trackerCategory}
          />
        )}
      </ImportExportStatusCellIconButton>
    );
  }
}
